import { create } from 'zustand';
import {
  confirmUpload,
  crudShift,
  deleteShift,
  fetchShift,
  fetchShiftList,
  FetchShiftListRequest,
  postShift,
  PostShiftRequest,
  PutShiftRequest,
  restoreShift,
  ShiftType,
  uploadShifts,
} from './api/shifts-api';
import { useAlertStore } from './use-alert-store';
import { EmployeeType } from './api/employees-api';
import { useEmployeesStore } from './use-employee-store';


type ShiftTempType = {
  staffIds: string[];
  startTime: string;
  endTime: string;
  startDate: Date;
  endDate: Date;
  weekDays: number[];
};

interface Store {
  postShift: (request: PostShiftRequest) => Promise<void>;
  crudShift: (request: Partial<PutShiftRequest>[]) => Promise<void>;
  fetchShiftList: (request: Partial<FetchShiftListRequest>) => Promise<void>;
  fetchShift: (request: { id: string }) => Promise<void>;
  restoreShift: (id: string) => Promise<void>;
  deleteShift: (id: string) => Promise<void>;
  list?: ShiftType[];
  shift?: Partial<ShiftTempType>;
  handleUpdateShift: (data: Partial<ShiftTempType>) => void;
  handleResetShift: () => void;
  isLoading?: boolean;

  uploadToken?: string;
  uploadedShifts?: ShiftType[];
  uploadShifts: (data: FormData) => Promise<void>;
  confirmUpload: () => Promise<void>;
}

const defaultEnabledWeekdays = [0, 1, 2, 3, 4]; // пн-пт в соответствии с локалью dayjs

export const useShiftsStore = create<Store>((set, get) => ({
  shift: {
    weekDays: defaultEnabledWeekdays,
  },
  handleUpdateShift: (data) => {
    set((prev) => ({
      ...prev,
      shift: {
        ...prev.shift,
        ...data,
      },
    }));
  },
  handleResetShift: () => {
    set({ shift: { weekDays: defaultEnabledWeekdays } });
  },
  postShift: async (request) => {
    set({ isLoading: true });
    try {
      const resp = await postShift(request);
      let updated = get().list || [];
      updated = [...updated, ...resp.created_shifts];
      set({ list: updated });
      useAlertStore.getState().add({ label: 'Смена успешно добавлена', variant: 'success' });
    } catch (e) {
      console.error(e);
      useAlertStore.getState().add({ label: 'Не удалось добавить смену', variant: 'danger' });
      throw e;
    } finally {
      set({ isLoading: false });
    }
  },
  crudShift: async (request) => {
    set({ isLoading: true });
    try {
      const resp = await crudShift(request);
      const deleted = resp?.deleted_shifts.map((item) => item.id);
      const updated = resp?.updated_shifts;
      const created = resp?.created_shifts;
      let updatedList = get().list || [];
      updatedList = updatedList?.filter((item) => !deleted?.includes(item.id));
      updatedList = updatedList?.map((item) => {
        const updatedItem = updated.find((upd) => upd.id === item.id);
        return updatedItem || item;
      });
      updatedList = [...updatedList, ...created];
      set({ list: updatedList });
      useAlertStore.getState().add({ label: 'Смены успешно обновлены ', variant: 'success' });
    } catch (e) {
      console.error(e);
      useAlertStore.getState().add({ label: 'Не удалось обновить смены', variant: 'danger' });
      throw e;
    } finally {
      set({ isLoading: false });
    }
  },
  fetchShiftList: async (request) => {
    set({ isLoading: true });
    try {
      const resp = await fetchShiftList(request);
      set({ list: resp });
    } catch (e) {
      console.error(e);
      useAlertStore.getState().add({ label: 'Не удалось получить смены', variant: 'danger' });
    } finally {
      set({ isLoading: false });
    }
  },
  fetchShift: async (request) => {
    set({ isLoading: true });
    try {
      await fetchShift(request);
    } catch (e) {
      console.error(e);
      useAlertStore.getState().add({ label: 'Не удалось получить смену', variant: 'danger' });
    } finally {
      set({ isLoading: false });
    }
  },
  restoreShift: async (id) => {
    set({ isLoading: true });
    try {
      await restoreShift(id);
    } catch (e) {
      console.error(e);
    } finally {
      set({ isLoading: false });
    }
  },
  deleteShift: async (id) => {
    set({ isLoading: true });
    try {
      await deleteShift(id);
    } catch (e) {
      console.error(e);
    } finally {
      set({ isLoading: false });
    }
  },
  uploadShifts: async (data) => {
    set({ isLoading: true });
    try {
      const resp = await uploadShifts(data);

      const baseEmployee: EmployeeType = {
        id: 0,
        name: {},
        patronymic: {},
        surname: {},
        tags: [],
        places: [],
        created_at: '',
        online: false,
        shift_state: 'NONE',
      };

      const employees: EmployeeType[] = Array.from(
        new Set<string>(resp.shifts?.map((item) => item.staff)),
      ).map((item, index) => {
        return {
          ...baseEmployee,
          id: index,
          name: { ru: item },
          shifts: [],
          tags: [{ id: '', value: resp.shifts.find((shift) => shift.staff === item)?.tag || '' }],
        };
      });
      useEmployeesStore.setState({ staff: employees });

      const mappedShifts: ShiftType[] = resp.shifts.map((item, index) => {
        return {
          ...item,
          id: String(index),
          staff: employees.find((emp) => emp.name.ru === item.staff)!,
        };
      });

      set({ uploadedShifts: mappedShifts, uploadToken: resp.upload_token });
    } catch (e) {
      useAlertStore.getState().add({ label: 'Не удалось загрузить смены', variant: 'danger' });
      console.error(e);
      throw e;
    } finally {
      set({ isLoading: false });
    }
  },
  confirmUpload: async () => {
    set({ isLoading: true });
    try {
      await confirmUpload(get().uploadToken || '');
      useAlertStore.getState().add({ label: 'Смены успешно загружены', variant: 'success' });
    } catch (e) {
      useAlertStore.getState().add({ label: 'Не удалось загрузить смены', variant: 'danger' });
      console.error(e);
    } finally {
      set({ isLoading: false });
    }
  },
}));
